import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiService } from '@/services/api-service';

/**
 * The onboarding checklist for one association (#523).
 *
 * Derived steps are computed by the server on every read, so this query is the ONLY source of
 * truth for them — there is nothing cached client-side that could disagree with the data. That
 * is also why `staleTime` is short: the point of a derived step is that it flips the moment its
 * evidence lands, and a long cache would report "owners imported" as still outstanding for
 * minutes after the import finished.
 */

export type OnboardingStepKind = 'derived' | 'attested';

export interface OnboardingStep {
	key: string;
	label: string;
	description: string;
	kind: OnboardingStepKind;
	done: boolean;
	href?: string;
	completedAt?: number | null;
	completedBy?: { id: string; name: string } | null;
	note?: string | null;
}

export interface OnboardingChecklist {
	associationId: string;
	associationName: string;
	steps: OnboardingStep[];
	completed: number;
	total: number;
	/** True when every step is done. The progress card hides itself on this. */
	complete: boolean;
	/**
	 * This community is IN ONBOARDING — it has an onboarding record.
	 *
	 * The card is gated on this rather than on an incomplete checklist. On preview, 56 of 57
	 * associations have accounts but only 2 have a bank-account row, so "incomplete" would put a
	 * permanent onboarding card on 55 established communities.
	 */
	started: boolean;
	startedAt?: number | null;
	startedBy?: { id: string; name: string } | null;
}

export const onboardingKeys = {
	all: ['associationOnboarding'] as const,
	one: (associationId: string) => [...onboardingKeys.all, associationId] as const,
};

export function useAssociationOnboarding(associationId: string | undefined) {
	return useQuery<OnboardingChecklist>({
		queryKey: onboardingKeys.one(associationId ?? ''),
		queryFn: () => apiService.fetchApi(
			`/association-onboarding/${associationId}`,
		) as Promise<OnboardingChecklist>,
		enabled: !!associationId,
		staleTime: 15_000,
		retry: false,
	});
}

/**
 * Tick or un-tick an ATTESTED step.
 *
 * The response is the whole recomputed checklist, so it is written straight into the cache
 * rather than triggering a refetch: the derived steps come back in the same payload, and a
 * separate invalidate would repaint the list twice for one click.
 */
export function useAttestOnboardingStep(associationId: string) {
	const queryClient = useQueryClient();
	return useMutation<OnboardingChecklist, Error, { stepKey: string; done: boolean; note?: string | null }>({
		mutationFn: ({ stepKey, done, note }) => apiService.fetchApi(
			`/association-onboarding/${associationId}/steps/${stepKey}`,
			{ method: 'PUT', body: JSON.stringify({ done, note: note ?? null }) },
		) as Promise<OnboardingChecklist>,
		onSuccess: (checklist) => {
			queryClient.setQueryData(onboardingKeys.one(associationId), checklist);
		},
	});
}

/**
 * Put a community INTO onboarding — for a takeover already in flight when this feature landed.
 *
 * Same cache write as attesting: the response is the recomputed checklist, so the card appears
 * without a second round trip.
 */
export function useStartOnboarding(associationId: string) {
	const queryClient = useQueryClient();
	return useMutation<OnboardingChecklist, Error, void>({
		mutationFn: () => apiService.fetchApi(
			`/association-onboarding/${associationId}/start`,
			{ method: 'POST', body: JSON.stringify({}) },
		) as Promise<OnboardingChecklist>,
		onSuccess: (checklist) => {
			queryClient.setQueryData(onboardingKeys.one(associationId), checklist);
		},
	});
}
